import { useState } from 'react';
import { esDate, fmt } from './format';
import type { SeoReport } from './types';

interface SeoSeasonChartProps {
    title: string;
    caption: string;
    report: SeoReport;
    field: 'clicks' | 'position';
    yoyField: 'yoy_clicks' | 'yoy_position';
    invert: boolean;
    digits: number;
}

const WIDTH = 640;
const HEIGHT = 240;
const PAD_L = 52;
const PAD_R = 16;
const PAD_T = 16;
const PAD_B = 36;

export function SeoSeasonChart({
    title,
    caption,
    report,
    field,
    yoyField,
    invert,
    digits,
}: SeoSeasonChartProps) {
    const points = report.season;
    const [hover, setHover] = useState<number | null>(null);
    const values = points.flatMap((point) => [point[field], point[yoyField]]).filter((value): value is number => value != null);
    const unit = report.grain === 'day' ? 'día' : 'semana';
    const axisTitle = field === 'clicks' ? `Clics por ${unit}` : 'Posición media';

    if (!values.length) {
        return (
            <section className="rounded-xl border border-border bg-card p-4">
                <h3 className="text-sm font-semibold text-foreground">{title}</h3>
                <p className="mt-3 text-sm text-muted-foreground">Sin serie.</p>
            </section>
        );
    }

    const min = invert ? Math.min(...values) : 0;
    let max = Math.max(...values);
    if (!invert) max = niceCeil(max);
    if (max <= min) max = min + 1;
    const plotW = WIDTH - PAD_L - PAD_R;
    const plotH = HEIGHT - PAD_T - PAD_B;
    const ticks = tickValues(min, max, invert ? 4 : 4);

    function x(index: number): number {
        if (points.length <= 1) return PAD_L;
        return PAD_L + (index * plotW) / (points.length - 1);
    }

    function y(value: number): number {
        const t = (value - min) / (max - min);
        return invert ? PAD_T + t * plotH : PAD_T + (1 - t) * plotH;
    }

    function line(key: typeof field | typeof yoyField): string {
        let d = '';
        points.forEach((point, index) => {
            const value = point[key];
            if (value == null) return;
            d += `${d ? ' L' : 'M'}${x(index)} ${y(value)}`;
        });
        return d;
    }

    const labelEvery = Math.max(1, Math.ceil(points.length / 8));
    const active = hover != null ? points[hover] : null;
    const activeWeek = hover != null ? report.weeks[hover] : null;

    return (
        <section className="relative rounded-xl border border-border bg-card p-4 min-w-0">
            <h3 className="text-sm font-semibold text-foreground">{title}</h3>
            <svg
                viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
                role="img"
                aria-label={title}
                className="mt-2 w-full h-auto text-muted-foreground"
                onMouseLeave={() => setHover(null)}
            >
                {ticks.map((tick) => (
                    <g key={tick}>
                        <line
                            x1={PAD_L}
                            x2={WIDTH - PAD_R}
                            y1={y(tick)}
                            y2={y(tick)}
                            stroke="currentColor"
                            strokeOpacity="0.25"
                        />
                        <text x={PAD_L - 8} y={y(tick) + 4} textAnchor="end" fontSize="11" fill="currentColor">
                            {fmt(tick, field === 'clicks' ? 0 : 1)}
                        </text>
                    </g>
                ))}
                <line x1={PAD_L} x2={PAD_L} y1={PAD_T} y2={HEIGHT - PAD_B} stroke="currentColor" strokeOpacity="0.7" />
                <line x1={PAD_L} x2={WIDTH - PAD_R} y1={HEIGHT - PAD_B} y2={HEIGHT - PAD_B} stroke="currentColor" strokeOpacity="0.7" />
                <text
                    x={14}
                    y={HEIGHT / 2}
                    textAnchor="middle"
                    fontSize="11"
                    fill="currentColor"
                    transform={`rotate(-90 14 ${HEIGHT / 2})`}
                >
                    {axisTitle}
                </text>
                {points.map((point, index) => (
                    index % labelEvery === 0 || index === points.length - 1 ? (
                        <text key={point.id} x={x(index)} y={HEIGHT - 12} textAnchor="middle" fontSize="11" fill="currentColor">
                            {axisTick(point.id, report.grain)}
                        </text>
                    ) : null
                ))}
                {line(yoyField) && (
                    <path d={line(yoyField)} fill="none" stroke={report.colors.cyan} strokeWidth="2" strokeDasharray="5 4" />
                )}
                {line(field) && (
                    <path d={line(field)} fill="none" stroke={report.colors.petrol} strokeWidth="2" />
                )}
                {points.map((point, index) => (
                    <g key={point.id}>
                        {point[yoyField] != null && (
                            <circle cx={x(index)} cy={y(point[yoyField] as number)} r="3.5" fill={report.colors.cyan} />
                        )}
                        {point[field] != null && (
                            <circle cx={x(index)} cy={y(point[field] as number)} r="3.5" fill={report.colors.petrol} />
                        )}
                        <rect
                            x={x(index) - plotW / Math.max(points.length, 1) / 2}
                            y={PAD_T}
                            width={plotW / Math.max(points.length, 1)}
                            height={plotH}
                            fill="transparent"
                            onMouseEnter={() => setHover(index)}
                        />
                    </g>
                ))}
            </svg>
            {active && activeWeek && (
                <div
                    className="pointer-events-none absolute z-10 max-w-[16rem] rounded-lg border border-border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-md"
                    style={{ left: `min(${(x(hover ?? 0) / WIDTH) * 100}%, calc(100% - 16rem))`, top: 48 }}
                >
                    <p className="font-semibold">{axisTick(active.id, report.grain)}</p>
                    <p className="text-muted-foreground">
                        {esDate(activeWeek.start)}{activeWeek.end !== activeWeek.start ? ` – ${esDate(activeWeek.end)}` : ''}
                    </p>
                    <p className="mt-1">Este año: {fmt(active[field], digits)} {field === 'clicks' ? 'clics' : 'de posición'}</p>
                    <p>Hace un año: {fmt(active[yoyField], digits)} {field === 'clicks' ? 'clics' : 'de posición'}</p>
                </div>
            )}
            <p className="mt-2 text-xs text-muted-foreground">{caption}</p>
        </section>
    );
}

function axisTick(id: string, grain: SeoReport['grain']): string {
    if (grain === 'day') {
        const parts = id.split('-');
        return parts.length === 3 ? `${parts[2]}/${parts[1]}` : id;
    }
    return id.replace(/^\d{4}-/, '');
}

function niceCeil(value: number): number {
    if (value <= 1) return 1;
    const pow = 10 ** Math.floor(Math.log10(value));
    const scaled = value / pow;
    const nice = scaled <= 1 ? 1 : scaled <= 2 ? 2 : scaled <= 5 ? 5 : 10;
    return nice * pow;
}

function tickValues(min: number, max: number, count: number): number[] {
    const step = (max - min) / count;
    return Array.from({ length: count + 1 }, (_, index) => Math.round((min + step * index) * 10) / 10);
}
